import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { mediaList, genreOptions, regionOptions, yearOptions, type MediaItem } from "@/mocks/movies";
import { categoryList, slugToType } from "@/mocks/categories";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb, { type Crumb } from "@/components/feature/Breadcrumb";
import FilterBar from "@/pages/category/components/FilterBar";
import Pagination from "@/pages/category/components/Pagination";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 12;

type SortKey = "update" | "hot";

function parseFilterToken(token?: string) {
  if (!token) return { genre: "全部", region: "全部", year: "全部" };
  const decoded = decodeURIComponent(token);
  if (yearOptions.includes(decoded) && decoded !== "全部") return { genre: "全部", region: "全部", year: decoded };
  if (regionOptions.includes(decoded) && decoded !== "全部") return { genre: "全部", region: decoded, year: "全部" };
  return { genre: decoded, region: "全部", year: "全部" };
}

export default function Category() {
  const { type, filter } = useParams();
  const meta = categoryList.find((c) => c.slug === type) ?? categoryList[0];
  const mediaType = slugToType[type ?? ""] ?? "电影";

  const init = parseFilterToken(filter);
  const [genre, setGenre] = useState(init.genre);
  const [region, setRegion] = useState(init.region);
  const [year, setYear] = useState(init.year);
  const [sort, setSort] = useState<SortKey>("update");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const p = parseFilterToken(filter);
    setGenre(p.genre);
    setRegion(p.region);
    setYear(p.year);
    setSort("update");
    setPage(1);
  }, [type, filter]);

  useDocumentMeta({
    title: `${meta.title} - 蘑菇视频高清影视资源`,
    description: `蘑菇视频${meta.title}频道为你精选${meta.description}`,
    keywords: `${meta.title},蘑菇视频,在线${meta.title},${meta.title}资源,影视推荐`,
  });

  const baseList = useMemo(() => mediaList.filter((m) => m.type === mediaType), [mediaType]);

  const filtered = useMemo(() => {
    let list: MediaItem[] = baseList.filter((m) => {
      const matchGenre = genre === "全部" || m.genre === genre || m.tags.includes(genre);
      const matchRegion = region === "全部" || m.region === region;
      const matchYear = year === "全部" || String(m.year) === year;
      return matchGenre && matchRegion && matchYear;
    });
    list = [...list].sort((a, b) => (sort === "hot" ? b.hot - a.hot : a.updateDate < b.updateDate ? 1 : -1));
    return list;
  }, [baseList, genre, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const crumbs: Crumb[] = [{ label: meta.title, to: filter ? `/category/${meta.slug}` : undefined }];
  if (filter) crumbs.push({ label: `筛选：${decodeURIComponent(filter)}` });

  const reset = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setPage(1);
  };

  const change = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPage(1);
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <Breadcrumb items={crumbs} />
          <div className="mt-4 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-background-50 text-2xl">
              <i className={meta.icon} aria-hidden="true"></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {meta.title}频道
              </h1>
              <p className="mt-1 text-sm text-foreground-500">{meta.subtitle}</p>
            </div>
          </div>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600">{meta.description}</p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <FilterBar
          genres={genreOptions}
          regions={regionOptions}
          years={yearOptions}
          genre={genre}
          region={region}
          year={year}
          onGenre={change(setGenre)}
          onRegion={change(setRegion)}
          onYear={change(setYear)}
          onReset={reset}
        />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-500">
            共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部作品
          </p>
          <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            {([
              { key: "update", label: "按更新时间", icon: "ri-time-line" },
              { key: "hot", label: "按热度", icon: "ri-fire-line" },
            ] as { key: SortKey; label: string; icon: string }[]).map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => {
                  setSort(s.key);
                  setPage(1);
                }}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                  sort === s.key ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                <i className={s.icon} aria-hidden="true"></i>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {pageItems.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
            <div className="mt-8">
              <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500 text-2xl">
              <i className="ri-inbox-line" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-base font-medium text-foreground-800">当前筛选条件下暂无内容</p>
            <p className="mt-1 text-sm text-foreground-500">试试调整或重置筛选条件，发现更多精彩影视。</p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              重置筛选
            </button>
          </div>
        )}
      </div>
    </div>
  );
}